import React, { forwardRef } from "react";

import {
  FormControl,
  OutlinedInput,
  Stack,
  Typography,
  Box,
} from "@mui/material";
import dayjs from "dayjs";
import { FieldValues } from "react-hook-form";

import { DatePicker } from "../DatePicker";
import { ErrorTypography } from "../Typography";

export type TextInputProps = {
  label: string;
  type: string;
  required: boolean;
  error: boolean;
  errorText?: string;
} & FieldValues;

export const TextFieldWithBirthDay = forwardRef<
  HTMLInputElement,
  TextInputProps
>(({ label, type, required, error, errorText, ...field }, ref) => {
  const todayString = dayjs(new Date()).format("YYYY-MM-DD");

  if (type === "date") {
    return (
      <FormControl variant="filled">
        <Stack spacing={1}>
          <Stack direction="row" spacing={0.5}>
            <Typography variant="bold14">{label}</Typography>
            {required && <Box color="alert.100">*</Box>}
          </Stack>
          <DatePicker
            {...field}
            inputRef={field.ref}
            value={field.value ? dayjs(field.value).toDate() : null}
            slotProps={{
              textField: {
                name: field.name,
                onBlur: field.onBlur,
                placeholder: "1980/01/01",
                inputProps: {
                  inputMode: "numeric" as const,
                  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                    // 制御キー（バックスペース、削除、Tab、Enter、矢印キー）は許可
                    const controlKeys = [
                      "Backspace",
                      "Delete",
                      "Tab",
                      "Enter",
                      "ArrowLeft",
                      "ArrowRight",
                      "ArrowUp",
                      "ArrowDown",
                      "Home",
                      "End",
                    ];
                    if (controlKeys.includes(e.key)) {
                      return;
                    }

                    // Ctrl/Cmd + キーの組み合わせは許可（コピー、貼り付けなど）
                    if (e.ctrlKey || e.metaKey) {
                      return;
                    }

                    // 数値、スラッシュ、ハイフンのみ許可
                    if (!/[0-9/-]/.test(e.key)) {
                      e.preventDefault();
                    }
                  },
                },
              },
            }}
            onChange={(newValue) => {
              const date = dayjs(newValue).format("YYYY-MM-DD");
              field.onChange(date ?? "");
            }}
          />
          {error && errorText && <ErrorTypography errorText={errorText} />}
        </Stack>
      </FormControl>
    );
  }

  return (
    <FormControl variant="filled">
      <Stack spacing={1}>
        <Stack direction="row" spacing={0.5}>
          <Typography variant="bold14">{label}</Typography>
          {required && <Box color="alert.100">*</Box>}
        </Stack>

        <OutlinedInput
          type={type}
          ref={ref}
          inputProps={{
            max: todayString,
            style: {
              padding: "13px 16px",
            },
          }}
          {...field}
        />
        {error && errorText && <ErrorTypography errorText={errorText} />}
      </Stack>
    </FormControl>
  );
});
